import React, { useState } from "react";
import { ArrowUpRight, ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { PORTFOLIO_IMG } from "@/data/content";

const APPROACH_CARDS = [
  {
    n: "01",
    t: "Real Asset Stewardship",
    b: "Investing in and operating institutional-grade real assets designed to deliver enduring utility, resilient value, and long-term relevance.",
    img: PORTFOLIO_IMG.thangLong,
  },
  {
    n: "02",
    t: "Healthspan Platforms",
    b: "Developing next-generation healthspan platforms that integrate clinical science, wellness infrastructure, artificial intelligence, and preventive healthcare.",
    img: PORTFOLIO_IMG.luxuryHealthcare,
  },
  {
    n: "03",
    t: "Investment in Human Capability",
    b: "Developing institutional knowledge and applied research to strengthen long-term innovation and organizational resilience.",
    img: "/assets/unsplash-1731458769726.jpg",
  },
];

const FRAMEWORK = [
  {
    structure: "Wholly Owned",
    whatItDoes: "Unik holds full equity and full control, keeping all of the risk and all of the return. This lets us set standards, governance, and brand without outside constraint.",
    whereWeApply: "Foundational and flagship assets, where control protects long-term value.",
    stage: "Early / Flagship",
  },
  {
    structure: "Strategic Partnerships",
    whatItDoes: "Unik brings in a long-term partner at the platform level. We accept some dilution in exchange for the partner's capital and operating capability, while keeping majority control and governance rights.",
    whereWeApply: "Platforms where the right partner's capital or capability can accelerate growth.",
    stage: "Growth",
  },
  {
    structure: "Co-investment Structures",
    whatItDoes: "Outside investors commit capital alongside Unik on a deal-by-deal basis. This widens our funding sources and keeps our capital efficient, while co-investors take defined, usually non-operating, governance roles.",
    whereWeApply: "Capital-intensive assets, where disciplined funding matters more than full ownership.",
    stage: "Capital-Intensive",
  },
  {
    structure: "Joint Ventures",
    whatItDoes: "Unik forms a dedicated company with a local or sector partner, and the two share both the risk and the return. The partner brings market knowledge we do not hold in-house.",
    whereWeApply: "New-market entry and platform replication, where local expertise reduces the risk of expansion.",
    stage: "Expansion",
  },
];

const COMPOSITION = [
  {
    n: "01",
    t: "Foundational Real Assets",
    b: "Long-term ownership in residential, office, and industrial assets, supported by leasing and management capability.",
    img: PORTFOLIO_IMG.berriver,
  },
  {
    n: "02",
    t: "Partnerships & Asset Management",
    b: "Collaborations with investors and strategic partners to access a broader opportunity set and deliver capital solutions aligned with long-term stewardship.",
    img: "/assets/1u8j3q98_partner.jpg",
  },
  {
    n: "03",
    t: "Chitta Healthspan Ecosystem",
    b: "Integrated platforms that advance human health, longevity, and well-being.",
    img: "/assets/4bkxivuc_CHITTA-GROUP.jpg",
  },
];

const EVOLUTION = [
  {
    phase: "Phase 1",
    when: "Inception",
    title: "Foundation",
    focus:
      "Core asset base established and operational. Healthspan platform concept developed and validated. Governance framework defined.",
    capital: [
      "· Real Assets ~70%",
      "· R&D ~15%",
      "· Technology ~10%",
      "· Operations ~5%",
    ],
    img: "/assets/zv2i0yeu_Long-Bien-Barrier.jpg",
  },
  {
    phase: "Phase 2",
    when: "2026 – 2027",
    title: "Platform Build",
    focus:
      "Institutional governance fully operational. Strategic partnerships secured. Capital integrated for platform scaling. CHI research capability established.",
    capital: [
      "· Real Assets ~75%",
      "· R&D ~12%",
      "· Technology ~10%",
      "· Operations ~3%",
      "· Target: ~USD 100M deployed",
    ],
    img: "/assets/ez9kvixa_chitta-center.jpg",
  },
  {
    phase: "Phase 3",
    when: "2028 – 2029",
    title: "Scale & Revenue",
    focus:
      "Chitta Retreat Phase 1 operational. Revenue generation and occupancy stabilisation achieved. Clinical model validated at scale. Technology integrated across platforms.",
    capital: [
      "· Real Assets ~72%",
      "· R&D ~13%",
      "· Technology ~12%",
      "· Operations ~3%",
      "· Target EBITDA margins: ~27–35%",
    ],
    img: "/assets/g4imvvul_Chitta-Retreat.jpg",
  },
  {
    phase: "Phase 4",
    when: "2030 +",
    title: "Ecosystem Maturity",
    focus:
      "Full ecosystem integration across assets, platforms, and services. Multiple revenue streams operational. Monetisation pathways established including potential IPO readiness.",
    capital: [
      "· Real Assets ~80%",
      "· R&D ~8%",
      "· Technology ~10%",
      "· Operations ~2%",
    ],
    img: "/assets/9eitpmqk_hanoi-building.jpg",
  },
];

const HOW_TO_INVEST = [
  {
    tag: "Partnership & Collaboration",
    title: "Build with us.",
    body:
      "Strategic partnerships and co-investment opportunities for aligned capital partners who share our time horizon and governance standards.",
    cta: "Partnership Inquiry",
    to: "/contact",
  },
  {
    tag: "Investment Opportunity",
    title: "Invest with us.",
    body:
      "Direct investment across our platform portfolio — for institutional investors, family offices, and qualified individuals seeking long-duration, asset-backed exposure.",
    cta: "Contact Us",
    to: "/contact",
  },
];

export default function Investments() {
  useReveal();

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow="Investment — I.GEN"
        title="Investment For"
        accent="Next Generation."
        sub="Capital deployed into sectors that will shape the future of human living — real assets, preventive healthcare, wellness infrastructure, and knowledge systems."
        bgImage="/assets/unsplash-1509030450996.jpg"
      />

      {/* 5.3.1 INVESTMENT APPROACH — elegant asymmetric editorial intro */}
      <section className="relative bg-white pt-28 lg:pt-40 pb-0">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
            {/* LEFT — eyebrow + headline */}
            <div className="lg:col-span-5" data-reveal>
              <div className="flex items-center gap-4">
                <span className="block w-12 h-px bg-unik-gold" />
                <div className="eyebrow text-[#14355a]">Investment Approach</div>
              </div>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 5.2vw, 5rem)",
                  fontWeight: 500,
                  lineHeight: "1.0",
                  letterSpacing: "-0.015em",
                }}
                className="mt-10 font-display text-[#14355a]"
              >
                Long-term <br/>
                <span className="text-unik-gold">structural</span> <br/>
                transformation.
              </h2>
            </div>

            {/* RIGHT — body paragraphs with elegant lede */}
            <div className="lg:col-span-7 lg:pt-4" data-reveal style={{ "--reveal-delay": "150ms" }}>
              <p
                className="font-display text-[#14355a] tracking-tight"
                style={{
                  fontSize: "clamp(1.25rem, 1.65vw, 1.5rem)",
                  fontWeight: 500,
                  lineHeight: 1.45,
                }}
              >
                Our investment philosophy is grounded in long-term structural
                transformation. We deploy capital into sectors that we believe
                will shape the future of human living — including real assets,
                preventive healthcare, wellness infrastructure, and knowledge
                systems.
              </p>
              <div className="mt-10 grid sm:grid-cols-2 gap-8 lg:gap-12 text-[16px] lg:text-[17px] leading-[1.9] text-black">
                <p>
                  We focus on opportunities where demographic change, rising
                  health awareness, urban evolution, and institutional demand
                  create enduring relevance across economic cycles.
                </p>
                <p>
                  Through disciplined capital allocation, operational
                  stewardship, and ecosystem thinking, we build scalable
                  platforms designed to generate sustainable returns,
                  resilient asset value, and long-term societal impact.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width alternating editorial rows — seamless, adjacent, fade-in on scroll */}
        <div className="mt-24 lg:mt-32 w-full">
          {APPROACH_CARDS.map((c, i) => {
            const reverse = i % 2 === 1;
            return (
              <div
                key={c.n}
                data-reveal-fade
                style={{ "--reveal-delay": `${i * 80}ms` }}
                className="grid lg:grid-cols-2 w-full"
              >
                {/* Image — full column width, edge-to-edge, no padding */}
                <div className={`${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.t}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content — generous internal padding, vertical center */}
                <div
                  className={`flex items-center bg-white ${reverse ? "lg:order-1" : ""}`}
                >
                  <div className="w-full px-8 sm:px-12 lg:px-20 py-14 lg:py-0 max-w-2xl mx-auto lg:mx-0">
                    <div
                      className="font-display text-unik-gold tracking-[0.2em]"
                      style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)", lineHeight: 1, fontWeight: 400 }}
                    >
                      {c.n}
                    </div>
                    <div className="mt-5 w-12 h-px bg-[#14355a]/30" />
                    <h3
                      style={{
                        fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)",
                        fontWeight: 500,
                        lineHeight: 1.1,
                      }}
                      className="mt-7 font-display text-[#14355a] tracking-tight"
                    >
                      {c.t}
                    </h3>
                    <p className="mt-7 text-[17px] lg:text-[18px] leading-[1.9] text-black max-w-md">
                      {c.b}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 5.3.2 INVESTMENT FRAMEWORK — dynamic progress flow */}
      <section className="relative bg-unik-cream py-28 lg:py-40">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal className="max-w-3xl mb-16 lg:mb-20">
            <div className="eyebrow text-[#14355a]">Investment Framework</div>
            <h2
              style={{
                fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
                fontWeight: 500,
                lineHeight: "1.0",
              }}
              className="mt-6 font-display text-[#14355a] tracking-tight"
            >
              Multiple structures. One discipline.
            </h2>
            <p className="mt-8 text-[18px] lg:text-[19px] leading-[1.95] text-unik-ink/80 max-w-3xl">
              Unik structures capital to match the maturity, capital intensity,
              and control needs of each platform. Because no single ownership
              model suits every asset, we choose from a defined set of
              structures and apply the one that best balances control, capital
              efficiency, and risk at each stage of a platform's life.
            </p>
          </div>

          {/* Interactive Framework Tabs — click to reveal details */}
          <FrameworkTabs items={FRAMEWORK} />
        </div>
      </section>

      {/* 5.3.3 PORTFOLIO COMPOSITION — pillars with images */}
      <section className="relative bg-white py-28 lg:py-40">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal className="max-w-3xl mb-16 lg:mb-20">
            <div className="eyebrow text-[#14355a]">Portfolio Composition</div>
            <h2
              style={{
                fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
                fontWeight: 500,
                lineHeight: "1.05",
              }}
              className="mt-6 font-display text-[#14355a] tracking-tight"
            >
              Three pillars of<br/>long-term capital.
            </h2>
          </div>
          {/* Static cards with images — no hover effect (no additional explanation) */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {COMPOSITION.map((c, i) => (
              <div
                key={c.n}
                data-reveal
                style={{ "--reveal-delay": `${i * 130}ms` }}
                className="relative bg-white border border-[#14355a]/10"
              >
                <div className="relative aspect-[5/3] overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.t}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0e2540]/40 via-transparent to-transparent" />
                  <div
                    className="absolute bottom-5 left-7 font-display text-white tracking-[0.18em]"
                    style={{ fontSize: "clamp(1.75rem, 2.4vw, 2.5rem)", fontWeight: 500, lineHeight: 1 }}
                  >
                    {c.n}
                  </div>
                </div>
                <div className="p-8 lg:p-10">
                  <h3
                    style={{ fontSize: "clamp(1.375rem, 1.75vw, 1.625rem)", fontWeight: 500 }}
                    className="font-display text-[#14355a] tracking-tight leading-tight"
                  >
                    {c.t}
                  </h3>
                  <p className="mt-5 text-[15px] lg:text-[16px] leading-[1.85] text-unik-ink/75">
                    {c.b}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5.3.4 PLATFORMS + 5.3.5 PORTFOLIO ASSETS — futuristic tab switcher */}
      <ExploreSwitcher />

      {/* 5.3.6 INVESTMENT EVOLUTION — removed per docx v6 */}

      {/* 5.3.7 HOW TO INVEST — compact, simple layout */}
      <section className="relative bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <div data-reveal className="text-center mb-12 lg:mb-14">
            <div className="eyebrow text-[#14355a]">How To Invest</div>
            <h2
              style={{
                fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                fontWeight: 500,
                lineHeight: "1.1",
              }}
              className="mt-5 font-display text-[#14355a] tracking-tight"
            >
              Two paths. One horizon.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-[#14355a]/15 max-w-3xl mx-auto">
            {HOW_TO_INVEST.map((h, i) => (
              <Link
                key={h.tag}
                to={h.to}
                data-reveal
                style={{ "--reveal-delay": `${i * 130}ms` }}
                className="group bg-white p-7 lg:p-9 hover:bg-unik-cream/40 transition-colors duration-500"
              >
                <div className="eyebrow text-unik-gold text-[10.5px]">{h.tag}</div>
                <h3
                  style={{ fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)", fontWeight: 500 }}
                  className="mt-4 font-display text-[#14355a] tracking-tight"
                >
                  {h.title}
                </h3>
                <p className="mt-4 text-[14px] lg:text-[15px] leading-[1.8] text-unik-ink/75">
                  {h.body}
                </p>
                <span className="link-rule mt-6 text-[#14355a] text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 group-hover:text-unik-gold">
                  {h.cta} <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow="Investor Relations"
        title="Invest with"
        accent="Unik."
        body="Speak with our capital partners team to learn more about strategy, governance, and partnership opportunities."
        primary={{ label: "Contact Us", to: "/contact" }}
        secondary={{ label: "About", to: "/about" }}
      />

      <SiteFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* EvolutionTabs — progress-bar tab with image, focus & capital lines  */
/* ------------------------------------------------------------------ */
function EvolutionTabs({ items }) {
  const [active, setActive] = useState(0);
  const current = items[active];
  return (
    <section className="relative bg-[#f3f4f6] py-28 lg:py-36" data-testid="evolution-tabs">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div data-reveal className="max-w-3xl mb-14 lg:mb-16">
          <div className="eyebrow text-[#14355a]">Investment Evolution</div>
          <h2
            style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", fontWeight: 500, lineHeight: "1.05" }}
            className="mt-6 font-display text-[#14355a] tracking-tight"
          >
            From foundation to ecosystem maturity.
          </h2>
          <p className="mt-7 text-[17px] leading-[1.95] text-unik-ink/80 max-w-2xl">
            Unik's investment evolution reflects a structured progression from
            foundational asset development to platform scaling and ecosystem
            monetization.
          </p>
        </div>

        {/* Progress bar tabs */}
        <div className="relative" data-reveal>
          <div className="absolute top-[18px] left-0 right-0 h-px bg-[#14355a]/15 z-0" />
          <div
            className="absolute top-[18px] left-0 h-[2px] bg-unik-gold z-10 transition-all duration-700 ease-out"
            style={{ width: `${((active + 1) / items.length) * 100}%` }}
          />
          <div className="relative z-10 grid grid-cols-4 gap-2 lg:gap-4">
            {items.map((t, i) => (
              <button
                key={t.phase}
                type="button"
                onClick={() => setActive(i)}
                data-testid={`evolution-tab-${i}`}
                className="group text-left"
              >
                <div className="flex items-center justify-start">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center font-display text-[13px] transition-all duration-500 ${
                      i <= active
                        ? "bg-unik-gold text-[#14355a]"
                        : "bg-white border border-[#14355a]/20 text-[#14355a]/55"
                    }`}
                    style={{ fontWeight: 600 }}
                  >
                    {i + 1}
                  </div>
                </div>
                <div
                  className={`mt-5 text-[10.5px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${
                    active === i ? "text-unik-gold" : "text-[#14355a]/55"
                  }`}
                >
                  {t.phase}
                </div>
                <div
                  className={`mt-2 font-display tracking-tight transition-colors duration-300 ${
                    active === i ? "text-[#14355a]" : "text-[#14355a]/60 group-hover:text-[#14355a]"
                  }`}
                  style={{ fontSize: "clamp(1rem, 1.4vw, 1.25rem)", fontWeight: 500, lineHeight: 1.2 }}
                >
                  {t.title}
                </div>
                <div className="mt-1 text-[12px] text-unik-ink/55 hidden md:block">
                  {t.when}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Active phase content */}
        <div
          key={active}
          className="mt-14 lg:mt-16 grid lg:grid-cols-12 gap-8 lg:gap-12 fade-in"
          data-testid="evolution-content"
        >
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={current.img} alt={current.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0e2540]/45 via-transparent to-transparent" />
              <div className="absolute top-6 left-6">
                <span className="inline-block px-4 py-1.5 bg-unik-gold text-[#14355a] font-display text-[10.5px] uppercase tracking-[0.22em]" style={{ fontWeight: 600 }}>
                  {current.phase}
                </span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="eyebrow text-unik-gold">{current.when}</div>
            <h3
              className="mt-5 font-display text-[#14355a] tracking-tight"
              style={{ fontSize: "clamp(2rem, 3vw, 2.75rem)", fontWeight: 500, lineHeight: 1.1 }}
            >
              {current.title}
            </h3>
            <p className="mt-7 text-[16px] lg:text-[17px] leading-[1.95] text-unik-ink/80">
              {current.focus}
            </p>
            <div className="mt-8 pt-7 border-t border-[#14355a]/15">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#14355a]/55 font-medium">
                Capital Allocation
              </div>
              <div className="mt-4 space-y-2 text-[15px] lg:text-[16px] text-black leading-[1.65]" style={{ fontWeight: 500 }}>
                {current.capital.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FrameworkTabs — interactive click-to-reveal stages                  */
/* ------------------------------------------------------------------ */
function FrameworkTabs({ items }) {
  const [active, setActive] = useState(0);
  const current = items[active];
  return (
    <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-stretch">
      {/* Left: stage selector */}
      <div className="lg:col-span-5 space-y-2">
        {items.map((f, i) => (
          <button
            key={f.structure}
            type="button"
            onClick={() => setActive(i)}
            data-testid={`framework-tab-${i}`}
            className={`group relative w-full text-left px-6 lg:px-7 py-5 lg:py-6 transition-all duration-500 border-l-2 ${
              active === i
                ? "border-unik-gold bg-white shadow-[0_4px_24px_rgba(20,53,90,0.06)]"
                : "border-transparent bg-white/40 hover:bg-white hover:border-[#14355a]/30"
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`text-[10.5px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${
                  active === i ? "text-unik-gold" : "text-[#14355a]/55"
                }`}
              >
                Stage 0{i + 1}
              </div>
              <div
                className={`flex-1 h-px transition-all duration-500 ${
                  active === i ? "bg-unik-gold/50" : "bg-[#14355a]/15"
                }`}
              />
              <ChevronRight
                size={16}
                className={`transition-all duration-500 ${
                  active === i
                    ? "text-unik-gold translate-x-0.5"
                    : "text-[#14355a]/30 group-hover:translate-x-0.5"
                }`}
              />
            </div>
            <div className="mt-4 flex items-end justify-between gap-3">
              <h3
                className="font-display text-[#14355a] tracking-tight"
                style={{ fontSize: "clamp(1.375rem, 1.9vw, 1.75rem)", fontWeight: 500, lineHeight: 1.15 }}
              >
                {f.structure}
              </h3>
              <span className="text-[12px] text-[#14355a]/55 font-display whitespace-nowrap">
                {f.stage}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Right: detail panel — animates on tab change */}
      <div className="lg:col-span-7">
        <div
          key={active}
          className="relative h-full min-h-[420px] bg-white border border-[#14355a]/10 p-10 lg:p-14 overflow-hidden fade-in"
          data-testid="framework-detail"
        >
          {/* Big background numeral */}
          <div
            className="absolute -top-4 right-6 font-display text-[#14355a]/[0.05] pointer-events-none select-none"
            style={{
              fontSize: "clamp(180px, 24vw, 320px)",
              lineHeight: 1,
              fontWeight: 500,
              letterSpacing: "-0.04em",
            }}
          >
            0{active + 1}
          </div>

          <div className="relative">
            <div className="flex items-center gap-3">
              <span className="w-10 h-px bg-unik-gold" />
              <div className="eyebrow text-unik-gold">{current.stage}</div>
            </div>
            <h3
              className="mt-7 font-display text-[#14355a] tracking-tight"
              style={{ fontSize: "clamp(2rem, 3.4vw, 3.25rem)", fontWeight: 500, lineHeight: 1.05 }}
            >
              {current.structure}
            </h3>

            {/* What it does */}
            <div className="mt-8">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#14355a]/55 font-medium">
                What it does
              </div>
              <p className="mt-3 text-[16px] lg:text-[17px] leading-[1.85] text-unik-ink/85 max-w-2xl">
                {current.whatItDoes}
              </p>
            </div>

            {/* Where we apply it */}
            <div className="mt-7 pt-7 border-t border-[#14355a]/15">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-unik-gold font-medium">
                Where we apply it
              </div>
              <p className="mt-3 text-[16px] lg:text-[17px] leading-[1.85] text-unik-ink/85 max-w-2xl">
                {current.whereWeApply}
              </p>
            </div>

            {/* Stage pagination dots */}
            <div className="mt-12 flex items-center gap-2">
              {items.map((item, i) => (
                <button
                  key={`stage-dot-${item.id ?? item.slug ?? item.title ?? i}`}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Show stage ${i + 1}`}
                  className={`h-[2px] transition-all duration-500 ${
                    active === i ? "w-12 bg-unik-gold" : "w-6 bg-[#14355a]/20 hover:bg-[#14355a]/40"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ExploreSwitcher — futuristic Platforms / Portfolio Assets toggler   */
/* ------------------------------------------------------------------ */
function ExploreSwitcher() {
  const TABS = [
    {
      id: "platforms",
      eyebrow: "Platforms",
      title: "Disciplined platforms. Enduring value.",
      body:
        "Four operating platforms across real estate, healthcare, and wellness — institutional structures through which we deploy capital and steward strategic assets.",
      to: "/investments/platforms",
      cta: "Explore Platforms",
      img: PORTFOLIO_IMG.chittaRetreat,
    },
    {
      id: "assets",
      eyebrow: "Portfolio Assets",
      title: "An active investor. A long-term builder.",
      body:
        "From flagship wellness estates to office towers, residential, and strategic landholdings — the operating real assets at the foundation of our portfolio.",
      to: "/investments/portfolio-assets",
      cta: "View Portfolio Assets",
      img: PORTFOLIO_IMG.berriver,
    },
  ];
  const [active, setActive] = useState(0);
  const current = TABS[active];

  return (
    <section
      data-testid="explore-switcher"
      className="relative text-white overflow-hidden bg-[#0e2540]"
    >
      <div className="absolute inset-0 silk-bg opacity-60" />
      <div className="absolute inset-0 grain pointer-events-none opacity-[0.05]" />
      {/* Decorative grid lines */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(233,195,123,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(233,195,123,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      {/* Radial gold accent */}
      <div
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(233,195,123,0.20), transparent 60%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
        <div data-reveal className="mb-12 lg:mb-14">
          <div className="eyebrow text-unik-gold">Explore</div>
          <div className="mt-6 w-12 h-px bg-unik-gold" />
        </div>

        {/* Animated toggle pill */}
        <div
          className="inline-flex items-center p-1.5 rounded-full bg-white/[0.06] border border-white/15 backdrop-blur-md mb-14 lg:mb-16"
          role="tablist"
        >
          {TABS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active === i}
              onClick={() => setActive(i)}
              data-testid={`explore-tab-${t.id}`}
              className={`relative px-7 lg:px-10 py-3 lg:py-3.5 rounded-full text-[11px] lg:text-xs font-display tracking-[0.18em] uppercase transition-colors duration-500 ${
                active === i ? "text-[#14355a]" : "text-white/70 hover:text-white"
              }`}
            >
              {active === i && (
                <span className="absolute inset-0 rounded-full bg-unik-gold transition-all duration-500 shadow-[0_4px_20px_rgba(233,195,123,0.35)]" />
              )}
              <span className="relative">{t.eyebrow}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Image with corner frame */}
          <div className="lg:col-span-6 order-1">
            <div className="relative aspect-[4/3] overflow-hidden">
              {TABS.map((t, i) => (
                <div
                  key={t.id}
                  aria-hidden={active !== i}
                  className={`absolute inset-0 transition-all duration-[1100ms] ease-out ${
                    active === i
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-105"
                  }`}
                >
                  <img src={t.img} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0e2540]/55 via-transparent to-transparent" />
                </div>
              ))}
              {/* Futuristic corner frames */}
              <div className="absolute top-0 left-0 w-12 h-12 lg:w-16 lg:h-16 border-t border-l border-unik-gold transition-all duration-700" />
              <div className="absolute top-0 right-0 w-8 h-8 lg:w-10 lg:h-10 border-t border-r border-unik-gold/40" />
              <div className="absolute bottom-0 left-0 w-8 h-8 lg:w-10 lg:h-10 border-b border-l border-unik-gold/40" />
              <div className="absolute bottom-0 right-0 w-12 h-12 lg:w-16 lg:h-16 border-b border-r border-unik-gold transition-all duration-700" />
              {/* Tab counter (e.g. 01/02) */}
              <div className="absolute bottom-6 right-6 flex items-baseline gap-2 font-display text-white">
                <span className="text-2xl lg:text-3xl text-unik-gold" style={{ fontWeight: 500 }}>
                  0{active + 1}
                </span>
                <span className="text-sm text-white/55">/ 0{TABS.length}</span>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="lg:col-span-6 order-2">
            <div key={active} className="fade-in">
              <h3
                className="font-display tracking-tight"
                style={{
                  fontSize: "clamp(2rem, 3.8vw, 3.5rem)",
                  fontWeight: 500,
                  lineHeight: "1.05",
                }}
              >
                {current.title}
              </h3>
              <p className="mt-8 max-w-xl text-white/80 text-[17px] lg:text-[18px] leading-[1.95]">
                {current.body}
              </p>
              <Link
                to={current.to}
                data-testid="explore-cta"
                className="group mt-12 inline-flex items-center gap-4 px-10 py-4 rounded-full bg-unik-gold text-[#14355a] font-display text-xs lg:text-sm tracking-[0.16em] uppercase hover:bg-white transition-all duration-500"
              >
                {current.cta}
                <ArrowUpRight
                  size={16}
                  className="group-hover:rotate-12 transition-transform duration-500"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
